import React, { useCallback, useMemo } from 'react';
import { useConfig } from '@rs-core/context/ConfigContext';
import { useAuth } from '@rs-core/hooks';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlined from '@mui/icons-material/PrintOutlined';

import PdfIcon from '@worklist-2/ui/src/assets/icons/UserProfile/PdfIcon.svg';
import downloadReport from '../../utils/downloadReport';

export default function BlumeReportHeader({ report, onClose }) {
	const { t } = useTranslation('homepage');
	const __config = useConfig();
	const { accessToken, sessionId } = useAuth();

	const reportTitle = useMemo(() => {
		const [{ title }] = report.resource.presentedForm;
		return title;
	}, [report]);

	const httpHeaders = useMemo(
		() => ({
			Accept: '*/*',
			Authorization: accessToken,
			SessionID: sessionId,
		}),
		[accessToken, sessionId]
	);

	const download = useCallback(() => {
		downloadReport(`${__config.data_sources.blume}Study/report/${report.resource.id}/content`, reportTitle);
	}, [report]);

	const print = useCallback(() => {
		const fileUrl = `${__config.data_sources.blume}Study/report/${report.resource.id}/content`;

		fetch(fileUrl, {
			headers: {
				...httpHeaders,
			},
		})
			.then(response => response.blob())
			.then(blob => {
				const pdfBlob = new Blob([blob], { type: 'application/pdf' });
				const url = URL.createObjectURL(pdfBlob);

				const printWindow = window.open(url, '_blank');
				printWindow.onload = () => {
					printWindow.print();
				};
			})
			.catch(_err => {
				console.log('Error printing the PDF file', _err);
			});
	}, [report, httpHeaders, __config]);

	return (
		<Box sx={STYLES.WRAPPER}>
			<Box sx={STYLES.HEADING_BOX}>
				<Avatar sx={STYLES.AVATAR}>
					<PdfIcon />
				</Avatar>
				<Typography sx={STYLES.REPORT_TITLE}>{reportTitle}</Typography>
			</Box>
			<Box sx={{ display: 'flex', gap: '24px' }}>
				<Button color="info" sx={STYLES.CLOSE_BUTTON} variant="outlined" onClick={onClose}>
					{t('Close')}
				</Button>
				<Button
					color="primary"
					startIcon={<PrintOutlined />}
					sx={STYLES.PRINT_BUTTON}
					variant="contained"
					onClick={print}
				>
					{t('Print')}
				</Button>
				<Button
					color="primary"
					startIcon={<FileDownloadOutlinedIcon />}
					sx={STYLES.DOWNLOAD_BUTTON}
					variant="contained"
					onClick={download}
				>
					{t('Download')}
				</Button>
			</Box>
		</Box>
	);
}

const BUTTON_STYLES = {
	height: '48px',
	maxHeight: '48px',
	borderRadius: '8px',
	textTransform: 'none',
	fontSize: 16,
	fontWeight: 600,
	boxShadow: 'none',
	whiteSpace: 'nowrap',
};

const STYLES = {
	WRAPPER: {
		width: '100%',
		padding: '20px',
		display: 'flex',
		justifyContent: 'space-between',
		boxSizing: 'border-box',
		backgroundColor: '#fff',
	},
	HEADING_BOX: {
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
	},
	AVATAR: {
		backgroundColor: '#fff',
		svg: {
			width: '32px',
		},
	},
	REPORT_TITLE: {
		color: 'rgba(54, 65, 82, 1)',
		fontSize: '20px',
		fontWeight: '500',
	},
	DOWNLOAD_BUTTON: {
		...BUTTON_STYLES,
		borderColor: 'var(--color-primary)',
		background: 'var(--color-primary)',
		color: 'white',

		'&.MuiButton-containedSizeMedium:hover': {
			backgroundColor: '#4096DC',
		},
	},
	PRINT_BUTTON: {
		...BUTTON_STYLES,
		background: 'rgba(235, 245, 254, 1)',
		color: 'rgba(66, 165, 245, 1)',

		'&.MuiButton-containedSizeMedium:hover': {
			backgroundColor: 'rgba(235, 245, 254, 1)',
		},
	},
	CLOSE_BUTTON: {
		...BUTTON_STYLES,
		borderColor: 'rgba(208, 213, 221, 1)',
		color: 'rgba(71, 84, 103, 1)',

		'&.MuiButton-containedSizeMedium:hover': {
			backgroundColor: '#4096DC',
		},
	},
};
